"use client";

import {
  FingerprintContext,
  FingerprintProvider as FingerprintReactProvider,
} from "@fingerprint/react";
import {
  useSyncExternalStore,
  type ComponentProps,
  type PropsWithChildren,
} from "react";

type FingerprintReactProviderProps = ComponentProps<
  typeof FingerprintReactProvider
>;

function parseFingerprintEndpoints(
  rawEndpoints: string | undefined
): FingerprintReactProviderProps["endpoints"] | undefined {
  if (!rawEndpoints) return undefined;

  const endpoints = rawEndpoints
    .split(",")
    .map((endpoint) => endpoint.trim())
    .filter(Boolean);

  if (endpoints.length === 0) return undefined;
  return endpoints.length === 1 ? endpoints[0] : endpoints;
}

const STUB_VALUE = {
  getVisitorData: async () => {
    throw new Error("Fingerprint is not configured");
  },
};

// useSyncExternalStore snapshot functions for Electron detection.
// Returns true on the client when running in Electron, false on SSR.
const subscribeNoop = () => () => {};
const getIsElectron = () =>
  typeof window !== "undefined" && window.electronAPI !== undefined;
const getIsElectronServer = () => false;

export function FingerprintProvider({ children }: PropsWithChildren) {
  const apiKey = process.env.NEXT_PUBLIC_FINGERPRINT_API_KEY;

  // Detect Electron without SSR/client hydration mismatch.
  // useSyncExternalStore returns false on the server and the real value
  // on the client, letting React handle the transition cleanly.
  const isElectron = useSyncExternalStore(
    subscribeNoop,
    getIsElectron,
    getIsElectronServer
  );

  if (!apiKey || isElectron) {
    return (
      <FingerprintContext.Provider value={STUB_VALUE}>
        {children}
      </FingerprintContext.Provider>
    );
  }

  const providerProps: FingerprintReactProviderProps = {
    apiKey,
  };

  const region = process.env.NEXT_PUBLIC_FINGERPRINT_REGION;
  if (region) {
    providerProps.region = region as FingerprintReactProviderProps["region"];
  }

  const endpoints = parseFingerprintEndpoints(
    process.env.NEXT_PUBLIC_FINGERPRINT_ENDPOINTS ||
      process.env.NEXT_PUBLIC_FINGERPRINT_ENDPOINT
  );
  if (endpoints) {
    providerProps.endpoints = endpoints;
  }

  return (
    <FingerprintReactProvider {...providerProps}>
      {children}
    </FingerprintReactProvider>
  );
}
